import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { verificationApi } from '@/api/verification';
import {
  VERIFICATION_PURPOSES,
  type PublicCredential,
  type RecentVerification,
  type VerificationOutcome,
  type VerificationPurpose,
} from '@/types/verification';
import { classifyQrContent } from '@/lib/qrContent';
import { useAsyncData } from '@/hooks';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { Select } from '@/components/ui/Select';
import { formatDateTime } from '@/utils/format';
import styles from './VerifyPage.module.css';

/**
 * Scan & verify — the staff console behind a hardware scanner or a paste.
 *
 * KEYBOARD-FIRST BY CONTRACT, not preference: card scanners are keyboard wedges
 * that type the code and press Enter, so the code field is autofocused, Enter
 * submits, and after a recorded scan focus returns to the emptied field for the
 * next person in line. Everything else on the page is reachable with Tab.
 *
 * TWO STEPS, DELIBERATELY. The scan first fetches the credential's identity
 * preview (name, photo, status) WITHOUT recording anything, so the operator can
 * look at the human in front of them before committing a verification. Only the
 * explicit confirm records. When the preview endpoint is not deployed yet (it
 * is being added in a parallel backend lane) the page says so and offers to
 * record directly — the fallback is honest, not silent.
 *
 * A REFUSAL IS DATA. The server answers HTTP 200 with a typed failureCode for
 * every refusal; the banner shows that code and the server's sentence verbatim,
 * because the terminal copy was written server-side for exactly this screen.
 */

const NS = 'verify';

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: PublicCredential }
  | { status: 'unavailable' } // endpoint not deployed — 404
  | { status: 'error'; message: string };

export function VerifyPage() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').split('-')[0];

  const [code, setCode] = useState('');
  const [purpose, setPurpose] = useState<VerificationPurpose>('SCHOOL_ARRIVAL');
  /** The code the current preview/confirm cycle is about — frozen at submit. */
  const [scanned, setScanned] = useState('');
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [outcome, setOutcome] = useState<VerificationOutcome | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);

  const codeRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const { state: recentState, reload: reloadRecent } = useAsyncData<RecentVerification[]>(
    'verification-recent',
    () => verificationApi.recent(),
    true,
  );

  // Focus moves to Confirm once an identity is on screen, so scan → glance →
  // Enter records without touching the mouse.
  useEffect(() => {
    if (preview.status === 'ready' || preview.status === 'unavailable') {
      confirmRef.current?.focus();
    }
  }, [preview.status]);

  const lookUp = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setScanned(trimmed);
    setOutcome(null);
    setRecordError(null);
    setPreview({ status: 'loading' });
    const classified = classifyQrContent(trimmed);
    // Dynamic tokens name their credential inside the payload; when that cannot
    // be read the raw content is used and the server decides.
    const reference = classified.credentialPublicId ?? trimmed;
    try {
      const data = await verificationApi.credentials.getPublic(reference);
      setPreview({ status: 'ready', data });
    } catch (cause) {
      if (cause instanceof ApiError && cause.isMissingEndpoint) {
        setPreview({ status: 'unavailable' });
        return;
      }
      setPreview({
        status: 'error',
        message: cause instanceof Error ? cause.message : t(`${NS}.preview.failed`),
      });
    }
  };

  const record = async () => {
    if (!scanned || recording) return;
    setRecording(true);
    setRecordError(null);
    try {
      const result = await verificationApi.verify({ rawQrContent: scanned, purpose });
      setOutcome(result);
      setPreview({ status: 'idle' });
      setScanned('');
      setCode('');
      reloadRecent();
      codeRef.current?.focus();
    } catch (cause) {
      // A transport/permission failure (403 from the purpose guard, network) —
      // distinct from a typed refusal, which arrives as a 200 outcome above.
      setRecordError(cause instanceof Error ? cause.message : t(`${NS}.record.failed`));
    } finally {
      setRecording(false);
    }
  };

  const reset = () => {
    setPreview({ status: 'idle' });
    setScanned('');
    setCode('');
    setRecordError(null);
    codeRef.current?.focus();
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.headerName}>{t(`${NS}.title`)}</h1>
        <p className={styles.headerSubtitle}>{t(`${NS}.subtitle`)}</p>
      </header>

      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          void lookUp(code);
        }}
      >
        <div className={styles.codeField}>
          <label className={styles.fieldLabel} htmlFor="verify-code">
            {t(`${NS}.codeLabel`)}
          </label>
          <p className={styles.fieldHint}>{t(`${NS}.codeHint`)}</p>
          <input
            id="verify-code"
            ref={codeRef}
            className={styles.codeInput}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            placeholder="crd_… / cvq.…"
          />
        </div>
        <div className={styles.purposeField}>
          <span className={styles.fieldLabel} id="verify-purpose-label">
            {t(`${NS}.purposeLabel`)}
          </span>
          <Select
            aria-labelledby="verify-purpose-label"
            value={purpose}
            onChange={(value) => setPurpose(value as VerificationPurpose)}
            options={VERIFICATION_PURPOSES.map((value) => ({
              value,
              label: t(`${NS}.purpose.${value}`),
            }))}
          />
        </div>
        <button type="submit" className={styles.submit} disabled={!code.trim()}>
          {t(`${NS}.check`)}
        </button>
      </form>

      {preview.status === 'loading' && (
        <p role="status" aria-live="polite" className={styles.muted}>
          {t(`${NS}.preview.loading`)}
        </p>
      )}

      {preview.status === 'error' && (
        <div className={`${styles.banner} ${styles.bannerFailure}`} role="alert">
          <div>
            <p className={styles.bannerTitle}>{t(`${NS}.preview.errorTitle`)}</p>
            {/* The server's sentence, verbatim. */}
            <p className={styles.bannerBody}>{preview.message}</p>
          </div>
        </div>
      )}

      {preview.status === 'unavailable' && (
        <div className={styles.unavailable} role="note">
          <p style={{ margin: 0 }}>
            <strong>{t(`${NS}.preview.missingTitle`)}</strong>{' '}
            {t(`${NS}.preview.missingBody`)}
          </p>
        </div>
      )}

      {preview.status === 'ready' && (
        <IdentityCard data={preview.data} locale={locale} />
      )}

      {(preview.status === 'ready' || preview.status === 'unavailable') && (
        <div className={styles.form}>
          <button
            type="button"
            ref={confirmRef}
            className={styles.submit}
            onClick={() => void record()}
            disabled={recording}
          >
            {recording
              ? t(`${NS}.record.submitting`)
              : t(`${NS}.record.confirm`, { purpose: t(`${NS}.purpose.${purpose}`) })}
          </button>
          <button type="button" className="modal-button modal-button--secondary" onClick={reset}>
            {t(`${NS}.record.cancel`)}
          </button>
        </div>
      )}

      {recordError && (
        <div className={`${styles.banner} ${styles.bannerFailure}`} role="alert">
          <div>
            <p className={styles.bannerTitle}>{t(`${NS}.record.errorTitle`)}</p>
            <p className={styles.bannerBody}>{recordError}</p>
          </div>
        </div>
      )}

      {outcome && <OutcomeBanner outcome={outcome} locale={locale} />}

      <section className={styles.section} aria-labelledby="verify-recent-title">
        <h2 id="verify-recent-title" className={styles.sectionTitle}>
          {t(`${NS}.recent.title`)}
        </h2>
        <p className={`${styles.fieldHint}`}>{t(`${NS}.recent.hint`)}</p>
        <AsyncPanel
          state={recentState}
          onRetry={reloadRecent}
          isEmpty={(rows) => rows.length === 0}
          emptyTitle={t(`${NS}.recent.empty`)}
          emptyBody={t(`${NS}.recent.emptyBody`)}
        >
          {(rows) => (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t(`${NS}.recent.when`)}</th>
                    <th>{t(`${NS}.recent.purpose`)}</th>
                    <th>{t(`${NS}.recent.result`)}</th>
                    <th>{t(`${NS}.recent.person`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.occurredAt, locale)}</td>
                      <td>
                        {t(`${NS}.purpose.${row.purpose}`, { defaultValue: row.purpose })}
                      </td>
                      <td>
                        {row.result === 'SUCCESS' ? (
                          t(`${NS}.recent.success`)
                        ) : (
                          <span className={styles.mono}>{row.failureCode ?? row.result}</span>
                        )}
                      </td>
                      <td>
                        {/* The endpoint returns ids, not names — shown as-is
                            rather than resolved through a lookup that does not
                            exist for this caller. */}
                        <span className={styles.mono}>{row.personId ?? '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AsyncPanel>
      </section>
    </div>
  );
}

function IdentityCard({ data, locale }: { data: PublicCredential; locale: string }) {
  const { t } = useTranslation();
  const blocked = data.status !== 'ACTIVE';
  const initials = data.person.displayName
    .split(/\s+/)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

  return (
    <div
      className={`${styles.identityCard} ${
        blocked ? styles.identityCardBlocked : styles.identityCardActive
      }`}
      role="status"
      aria-live="polite"
    >
      {data.person.photographUrl ? (
        <img className={styles.identityPhoto} src={data.person.photographUrl} alt="" />
      ) : (
        <span className={styles.identityInitials} aria-hidden="true">
          {initials || '—'}
        </span>
      )}
      <div className={styles.identityBody}>
        <p className={styles.identityName}>{data.person.displayName}</p>
        <div className={styles.identityMeta}>
          <StateBadge kind="credential" value={data.status} />
          <span>
            {t(`credentials.type.${data.credentialType}`, { defaultValue: data.credentialType })}
          </span>
          {data.expiresAt && (
            <span>
              {t(`${NS}.preview.expires`, { date: formatDateTime(data.expiresAt, locale) })}
            </span>
          )}
        </div>
        <span className={styles.mono}>{data.credentialPublicId}</span>
      </div>
    </div>
  );
}

function OutcomeBanner({ outcome, locale }: { outcome: VerificationOutcome; locale: string }) {
  const { t } = useTranslation();

  if (outcome.result === 'SUCCESS') {
    return (
      <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status" aria-live="polite">
        <div>
          <p className={styles.bannerTitle}>
            {t(`${NS}.outcome.successTitle`, { name: outcome.person?.displayName ?? '' })}
          </p>
          <p className={styles.bannerBody}>
            {t(`${NS}.outcome.successBody`, {
              purpose: t(`${NS}.purpose.${outcome.purpose ?? ''}`, {
                defaultValue: outcome.purpose ?? '',
              }),
              time: outcome.occurredAt ? formatDateTime(outcome.occurredAt, locale) : '',
            })}
            {outcome.duplicate && <> {t(`${NS}.outcome.duplicate`)}</>}
            {outcome.attendance && (
              <>
                {' '}
                {outcome.attendance.recorded
                  ? t(`${NS}.outcome.attendanceRecorded`)
                  : t(`${NS}.outcome.attendancePending`)}
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.banner} ${styles.bannerFailure}`} role="alert">
      <div>
        <p className={styles.bannerTitle}>
          {t(`${NS}.outcome.refusedTitle`)}{' '}
          {/* The typed failure name, verbatim — the audit trail speaks this
              vocabulary and the operator may need to read it down a phone. */}
          <span className={styles.bannerCode}>{outcome.failureCode ?? 'FAILURE'}</span>
        </p>
        {outcome.message && <p className={styles.bannerBody}>{outcome.message}</p>}
      </div>
    </div>
  );
}
